import { throttle } from 'lodash-es';

import logger from '../../services/logging/logging-service';
import { getCoords, getHeight, hideElement, isHiddenElement, unHideElement } from '../../utils/dom-utils';
import { isMobile } from '../../utils/device-utils';
import { isLandscape } from '../../utils/mediaquery-utils';
import {
  isElectionsPage,
  isFreeHtmlPage,
  isGalleryPage,
  isHoroscopePages,
  isLatestNewsPage,
  isSportDataPage,
} from '../../utils/aem-utils';
import { commonStore, getCommonStorePartial } from '../../global-store/subjects/common';
import { COMMON_STATE_KEYS } from '../../global-store/subjects/common/initial-state';
import { VIDEO_PLAYER_OBSERVER_OPTIONS, VIDEO_PLAYER_POSITIONS, VIDEO_PLAYER_STATUS } from '../video-player/constants';
import { initVideoCarouselListeners } from '../video-player/helpers';

const SCROLL_EVENT_THROTTLE_TIME = 500;

const HOOK_CLOSE = '.j-player-card__close';
const HOOK_CAROUSEL_ELEMENT = '.j-player-carousel-element';
const HOOK_AUTOSTART = '#j-player-with-carousel';
const HOOK_CAROUSE_ELEMENT_STICKY_CONTAINER = '#video-with-carousel-section-container';
const HOOK_CAROUSE_ELEMENT_STICKY_SPACE = '.j-player-carousel-container';
const HOOK_PLAYER_TYPE = '.j-player-carousel';
const CLASS_ELEMENT_TO_SPACE_STICKY = 'o-aspect-ratio--16-9';
const HOOK_ABSTRACT = '.c-article-abstract';
const HOOK_GLOBAL_NAV = '.j-global-nav';
const HOOK_PLAYER_CARTEL = '.j-player-carousel .skyplayer-preview-container';
const HOOK_PLAYER_CARTEL_FALL_BACK = '.j-player-carousel #j-player-with-carousel_jwplayer';
const HOOK_LOCAL_NAV = '.j-local-nav';
const HOOK_Z_INDEX_LOCAL_OPEN = 'u-video-sticky-local-open';
const TOLERANCE_SPACE = 250;

const MOBILE_SET = 'mobile';
const ALL_SET = 'all';
const DESKTOP_SET = 'desktop';

// the element that goes into the sticky position
const $videoCarouselSticky = document.querySelector(HOOK_CAROUSEL_ELEMENT);
const $videoCarouselStickyContainer = document.querySelector(HOOK_CAROUSE_ELEMENT_STICKY_CONTAINER);
const $videoCarouselStickyHeader = $videoCarouselSticky?.querySelector(HOOK_CLOSE);
const $videoElemenCarouseltStickyContainerSpace = document.querySelector(HOOK_CAROUSE_ELEMENT_STICKY_SPACE);
const $localNav = document.querySelector(HOOK_LOCAL_NAV);

const stickyCarouselActive = $videoCarouselSticky?.dataset?.stickycarouselactive === 'true';
const firstVideoSticky = $videoCarouselSticky?.dataset?.firstvideosticky === 'true';
const stickyDeviceMode = $videoCarouselSticky?.dataset?.stickydevicemode;
const shouldInitCarousel =
  stickyCarouselActive &&
  (stickyDeviceMode === ALL_SET ||
    (isMobile() && stickyDeviceMode === MOBILE_SET) ||
    (!isMobile() && stickyDeviceMode === DESKTOP_SET));
let playertype = $videoCarouselSticky?.querySelector(HOOK_PLAYER_TYPE)?.dataset?.playertype;
let autoStart = $videoCarouselSticky?.querySelector(HOOK_AUTOSTART)?.dataset?.autostart === 'true';
let isClosed = false;
let isScrolled = false;
let elementInCarouselHeight;
let hideStickyTimeOut;

const shouldSticky = (scrollingPage, scrollingElement) => scrollingPage > scrollingElement;

// Function that checks if the element can go sticky in terms of page height.
// If the page width is greater than the height of the container it is possible to put the element in sticky
const mustSticky = () => {
  if (isSportDataPage() || isHoroscopePages() || isFreeHtmlPage() || isLatestNewsPage() || isElectionsPage()) {
    return true;
  }
  if (isGalleryPage()) {
    const scrollElement = getCoords(document.querySelector(HOOK_ABSTRACT)).top;
    return shouldSticky(window.scrollY, scrollElement);
  }

  const scrollElement = getCoords($videoCarouselStickyContainer).top;
  return shouldSticky(window.scrollY, scrollElement);
};

/**
 * Set video position
 * @param {*} element sticky video element
 * @param {*} player player instance
 * @param {*} position position to set ('sticky' or 'default')
 */
const setVideoCarouselPosition = (element, player, position) => {
  playertype = $videoCarouselSticky.querySelector(HOOK_PLAYER_TYPE).dataset.playertype;
  switch (position) {
    case VIDEO_PLAYER_POSITIONS.DEFAULT:
      if (element.dataset.state === VIDEO_PLAYER_POSITIONS.STICKY) {
        element.dataset.state = VIDEO_PLAYER_POSITIONS.DEFAULT;
        player.resize();
        hideElement($videoCarouselStickyHeader);
        player.setContext('page');
      }
      break;
    case VIDEO_PLAYER_POSITIONS.STICKY:
      element.dataset.state = VIDEO_PLAYER_POSITIONS.STICKY;
      player.setContext(playertype);
      break;
    default:
      logger.info('[ STICKY - CAROUSEL ] video is not sticky and it is not in default state.');
      break;
  }
  player.resize();
};

// Destruction of the StickyCarousel
const destroyStickyCarousel = (observer, CarouselStickyContainer, CarouselSticky, videoPlayer) => {
  isClosed = true;
  observer.unobserve(CarouselStickyContainer);
  setVideoCarouselPosition(CarouselSticky, videoPlayer, VIDEO_PLAYER_POSITIONS.DEFAULT);
  $videoElemenCarouseltStickyContainerSpace.classList.remove(CLASS_ELEMENT_TO_SPACE_STICKY);
  logger.warn('[ STICKY - CAROUSEL ] Destroy the StickyCarousel, for an error or for clos the banner.');
};

// Landscape management, if the element goes into landscape the sticky is not delivered
const settingLandscape = (videoPlayer, observerCarouselSticky) => {
  if (isMobile()) {
    let currentViewportWidth = window.innerWidth;
    let currentViewportHeight = window.innerHeight;
    if (isLandscape()) {
      destroyStickyCarousel(observerCarouselSticky, $videoCarouselStickyContainer, $videoCarouselSticky, videoPlayer);
    }
    window.addEventListener(
      'resize',
      throttle(() => {
        if (window.innerWidth !== currentViewportWidth || window.innerHeight !== currentViewportHeight) {
          if (isLandscape()) {
            destroyStickyCarousel(
              observerCarouselSticky,
              $videoCarouselStickyContainer,
              $videoCarouselSticky,
              videoPlayer
            );
          }
          currentViewportWidth = window.innerWidth;
          currentViewportHeight = window.innerHeight;
        }
      }, SCROLL_EVENT_THROTTLE_TIME)
    );
  }
};

const handleErrorPlayer = ({ errorName, observerCarouselSticky, videoPlayer }) => {
  destroyStickyCarousel(observerCarouselSticky, $videoCarouselStickyContainer, $videoCarouselSticky, videoPlayer);
  logger.error(`[ STICKY - CAROUSEL ] ${errorName}`);
  autoStart = false;
  $videoCarouselSticky.querySelector(HOOK_PLAYER_TYPE).style.height = `${elementInCarouselHeight}px`;
};

// STATUS HANDLERS
const getStatusHandlers = (videoPlayer, observerCarouselSticky) => ({
  [VIDEO_PLAYER_STATUS.INITIAL]: () => {},
  [VIDEO_PLAYER_STATUS.AD_ERROR]: () => {},
  [VIDEO_PLAYER_STATUS.READY]: () => {
    if (!elementInCarouselHeight) {
      elementInCarouselHeight =
        getHeight(document.querySelector(HOOK_PLAYER_CARTEL)) ||
        getHeight(document.querySelector(HOOK_PLAYER_CARTEL_FALL_BACK));
    }
    if (autoStart) {
      if ($videoCarouselSticky.dataset.state === VIDEO_PLAYER_POSITIONS.STICKY) {
        setVideoCarouselPosition($videoCarouselSticky, videoPlayer, VIDEO_PLAYER_POSITIONS.STICKY);
      }
      if (
        !isHiddenElement($videoCarouselStickyHeader) &&
        $videoCarouselSticky.dataset.state === VIDEO_PLAYER_POSITIONS.STICKY
      ) {
        hideElement($videoCarouselStickyHeader);
      }
      logger.info('[ STICKY - CAROUSEL ] STATUS_READY');
    }
  },
  [VIDEO_PLAYER_STATUS.REPLAY_CLICK]: () => {
    logger.info('[ STICKY - CAROUSEL ] STATUS_REPLAY_CLICK');
    if (
      isHiddenElement($videoCarouselStickyHeader) &&
      $videoCarouselSticky.dataset.state === VIDEO_PLAYER_POSITIONS.STICKY
    ) {
      unHideElement($videoCarouselStickyHeader);
    }
    clearTimeout(hideStickyTimeOut);
  },
  [VIDEO_PLAYER_STATUS.PLAY]: () => {
    autoStart = true;
    logger.info('[ STICKY - CAROUSEL ] STATUS_PLAY');
    if (
      isHiddenElement($videoCarouselStickyHeader) &&
      $videoCarouselSticky.dataset.state === VIDEO_PLAYER_POSITIONS.STICKY
    ) {
      unHideElement($videoCarouselStickyHeader);
    }
  },
  [VIDEO_PLAYER_STATUS.PAUSE]: () => {
    logger.info('[ STICKY - CAROUSEL ] PAUSE');
    hideElement($videoCarouselStickyHeader);
  },
  [VIDEO_PLAYER_STATUS.AD_PAUSE]: () => {
    logger.info('[ STICKY - CAROUSEL ] AD PAUSE');
    hideElement($videoCarouselStickyHeader);
  },
  [VIDEO_PLAYER_STATUS.AD_PLAY]: () => {
    autoStart = true;
    logger.info('[ STICKY - CAROUSEL ] STATUS_AD_PLAY');
    hideElement($videoCarouselStickyHeader);
  },
  [VIDEO_PLAYER_STATUS.COMPLETE]: () => {
    logger.info('[ STICKY - CAROUSEL ] STATUS_COMPLETE');
    if (firstVideoSticky) {
      hideStickyTimeOut = setTimeout(() => {
        logger.info('[ STICKY - CAROUSEL ] STATUS_COMPLETE and setting is for firstVideoSticky');
        $videoElemenCarouseltStickyContainerSpace.classList.remove(CLASS_ELEMENT_TO_SPACE_STICKY);
        hideElement($videoCarouselStickyHeader);
        destroyStickyCarousel(observerCarouselSticky, $videoCarouselStickyContainer, $videoCarouselSticky, videoPlayer);
      }, 3000);
    }
  },
  [VIDEO_PLAYER_STATUS.ERROR]: () => {
    handleErrorPlayer({ errorName: VIDEO_PLAYER_STATUS.ERROR, observerCarouselSticky, videoPlayer });
  },
  [VIDEO_PLAYER_STATUS.SETUP_ERROR]: () => {
    handleErrorPlayer({ errorName: VIDEO_PLAYER_STATUS.SETUP_ERROR, observerCarouselSticky, videoPlayer });
  },
  [VIDEO_PLAYER_STATUS.GENERIC_ERROR]: () => {
    handleErrorPlayer({ errorName: VIDEO_PLAYER_STATUS.GENERIC_ERROR, observerCarouselSticky, videoPlayer });
  },
  [VIDEO_PLAYER_STATUS.HLS_STATUS_ERROR]: () => {
    handleErrorPlayer({ errorName: VIDEO_PLAYER_STATUS.HLS_STATUS_ERROR, observerCarouselSticky, videoPlayer });
  },
});

/**
 * Create header and x for close element
 */

export const stickyCarousel = (player) => {
  if (shouldInitCarousel) {
    const videoPlayer = player;
    // call back of the observer, check if the element is in view or not in order to manage its status
    const observerCarouselCallBack = (entries) => {
      const videoCarouselStatus = commonStore.getValue().videoCarouselStatus;
      entries.forEach((entry) => {
        if (entry.intersectionRatio > 0.6) {
          $videoElemenCarouseltStickyContainerSpace.classList.remove(CLASS_ELEMENT_TO_SPACE_STICKY);
          setVideoCarouselPosition($videoCarouselSticky, videoPlayer, VIDEO_PLAYER_POSITIONS.DEFAULT);
          hideElement($videoCarouselStickyHeader);
          if (!autoStart) {
            logger.info('[ STICKY - CAROUSEL ] in view');
          } else {
            logger.info('[ STICKY - CAROUSEL ] sticky Carousel not run because the autoStart is set to -->', autoStart);
            logger.info('[ STICKY - CAROUSEL ] in view');
          }
        } else {
          logger.info('[ STICKY - CAROUSEL ] out of view');
          let bottomFlag;
          const scrollingPage = window.innerHeight + window.scrollY;
          const pageBodyHeight = document.body.offsetHeight;
          const difference = pageBodyHeight - scrollingPage;
          if (difference <= TOLERANCE_SPACE) {
            bottomFlag = true;
          }
          if (mustSticky() && autoStart && !isClosed && !bottomFlag) {
            setVideoCarouselPosition($videoCarouselSticky, videoPlayer, VIDEO_PLAYER_POSITIONS.STICKY);
            $videoElemenCarouseltStickyContainerSpace.classList.add(CLASS_ELEMENT_TO_SPACE_STICKY);
            const isCloseForbidden =
              videoCarouselStatus === VIDEO_PLAYER_STATUS.PAUSE ||
              videoCarouselStatus === VIDEO_PLAYER_STATUS.AD_PAUSE ||
              videoCarouselStatus === VIDEO_PLAYER_STATUS.AD_PLAY;
            if (isCloseForbidden) {
              hideElement($videoCarouselStickyHeader);
            } else {
              unHideElement($videoCarouselStickyHeader);
            }
          }
        }
        videoPlayer.resize();
      });
    };

    const observerCarouselSticky = new IntersectionObserver(observerCarouselCallBack, VIDEO_PLAYER_OBSERVER_OPTIONS);

    // the logic run only after first scroll
    const runStickyValue = () => {
      if (isScrolled) {
        return;
      }
      isScrolled = true;
      logger.info('[ STICKY - CAROUSEL ] Run sticky Carousel');
      observerCarouselSticky.observe($videoCarouselStickyContainer);
    };

    const scrollCallback = () => {
      if (isSportDataPage()) {
        const globalNavScroll = getCoords(document.querySelector(HOOK_GLOBAL_NAV)).top;
        if (shouldSticky(window.scrollY, globalNavScroll)) {
          runStickyValue();
        }
      } else if (isGalleryPage()) {
        const abstractScroll = getCoords(document.querySelector(HOOK_ABSTRACT)).top;
        if (shouldSticky(window.scrollY, abstractScroll)) {
          runStickyValue();
        }
      } else if (!isGalleryPage()) {
        runStickyValue();
      }
    };

    // the logic run only after first scroll launch callback
    window.addEventListener('scroll', scrollCallback);

    // check status of the player
    initVideoCarouselListeners(videoPlayer);

    // Partial store subscription
    const key = COMMON_STATE_KEYS.VIDEO_CAROUSEL_STATUS;
    const videoCarouselStore = getCommonStorePartial(key);
    videoCarouselStore.subscribe((videoCarouselStatus) => {
      const handleStatus = getStatusHandlers(videoPlayer, observerCarouselSticky)[videoCarouselStatus];
      handleStatus();
    });

    // create sticky Header with title and x for close
    $videoCarouselStickyHeader.addEventListener('click', () => {
      videoPlayer.pause();
      $videoElemenCarouseltStickyContainerSpace.classList.remove(CLASS_ELEMENT_TO_SPACE_STICKY);
      hideElement($videoCarouselStickyHeader);
      destroyStickyCarousel(observerCarouselSticky, $videoCarouselStickyContainer, $videoCarouselSticky, videoPlayer);
      videoPlayer.resize();
    });

    $localNav.addEventListener('click', () => {
      if ($videoCarouselStickyHeader.classList.contains(HOOK_Z_INDEX_LOCAL_OPEN)) {
        $videoCarouselSticky.classList.remove(HOOK_Z_INDEX_LOCAL_OPEN);
      } else {
        $videoCarouselSticky.classList.add(HOOK_Z_INDEX_LOCAL_OPEN);
      }
      videoPlayer.pause();
      logger.info('[ STICKY - CAROUSEL ] open local nav');
    });

    // Logic for settings display of sticky element.
    settingLandscape(player, observerCarouselSticky);
  }
};
